// Тонкая обёртка над window.Telegram.WebApp.
// Глобальный объект подгружается из telegram-web-app.js в index.html.

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  ready: () => void;
  expand: () => void;
  close: () => void;
  MainButton: {
    text: string;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    setText: (t: string) => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
  };
  // Нативный диалог подтверждения Telegram (Bot API 6.2+). callback(true) — «ОК».
  showConfirm?: (message: string, callback?: (confirmed: boolean) => void) => void;
  // Нативное информационное окно Telegram (Bot API 6.2+).
  showAlert?: (message: string, callback?: () => void) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getTelegram(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function isInTelegram(): boolean {
  const tg = getTelegram();
  return !!tg && !!tg.initData && tg.initData.length > 0;
}

// Диалог подтверждения: предпочитаем нативный Telegram.showConfirm, иначе window.confirm.
// Возвращает Promise<boolean> — true, если пользователь нажал «ОК».
// ВАЖНО: в web-версии (вне Telegram) объект Telegram.WebApp всё равно подгружен
// скриптом telegram-web-app.js, и метод showConfirm СУЩЕСТВУЕТ, но его вызов вне
// клиента Telegram кидает WebAppMethodUnsupported. Поэтому нативные диалоги
// используем только когда реально внутри Telegram (isInTelegram), плюс
// подстраховываемся try/catch с откатом на window.confirm.
export function confirmDialog(message: string): Promise<boolean> {
  const tg = getTelegram();
  if (isInTelegram() && tg?.showConfirm) {
    try {
      return new Promise<boolean>(resolve => {
        tg.showConfirm!(message, confirmed => resolve(!!confirmed));
      });
    } catch {
      /* WebAppMethodUnsupported и т.п. — падаем в window.confirm ниже */
    }
  }
  return Promise.resolve(window.confirm(message));
}

// Информационное окно: предпочитаем нативный Telegram.showAlert, иначе window.alert.
// См. комментарий к confirmDialog про web-версию вне Telegram.
export function alertDialog(message: string): void {
  const tg = getTelegram();
  if (isInTelegram() && tg?.showAlert) {
    try {
      tg.showAlert(message);
      return;
    } catch {
      /* WebAppMethodUnsupported — падаем в window.alert ниже */
    }
  }
  window.alert(message);
}
